import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import * as _ from 'lodash';
import {ActivatedRoute} from '@angular/router';
import {SymbolsService} from '../../services/symbols.service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {TableService} from '../../services/table.service';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {saveAs} from 'file-saver';
import {DatePipe} from '@angular/common';
import {QedSymbolsInfo} from '../../models/qedSymbolsInfo';
import {QedSymbol} from '../../models/qedSymbol';
import {BaseClientComponent} from '../base-client.component';

@Component({
  selector: 'app-qrd-multicast-list',
  templateUrl: './qrd-symbol-data.component.html',
  styleUrls: ['./qrd-symbol-data.component.scss'],
  host: {class: 'sym-page'}
})
export class QrdSymbolDataComponent extends BaseClientComponent implements OnInit {
  researchForm: UntypedFormGroup;
  listQrdSymbols: QedSymbol[];
  marketList = [];
  qrdSymbolsInfo: QedSymbolsInfo;
  qrdSymbolsInfoData: any;
  alertQedSymbolHasNotChanged = false;
  cols = [];
  qrdProductDropdown: SelectItem[];
  downloadOption = 'dateRange';
  filteredQrdSymbols: QedSymbol[];
  public startDates: string[];
  minDateForCalendar: Date = new Date('2019-01-01');
  maxDateForCalendar: Date = null;
  dateArray: any[];
  disabledDatesInCalendar: any[];
  public listQrdSymbolsWithSfi: any[] = [];
  public newsOnly = false;
  displayPastListsDialog = false;

  userChoicesFiltersDefaultQrdSymbols = {
    qrdProduct: '',
    channel: '',
    description: ''
  };

  userChoicesActiveFilters = _.clone(this.userChoicesFiltersDefaultQrdSymbols);

  constructor(private symbolsService: SymbolsService,
              public authenticationService: AuthenticationService,
              private route: ActivatedRoute,
              public tableService: TableService,
              private utilsService: UtilsService,
              public datePipe: DatePipe,
              private httpClient: HttpClient,
              private formBuilder: UntypedFormBuilder) {
    super();
    this.maxDateForCalendar = new Date();
    this.maxDateForCalendar.setMonth(this.maxDateForCalendar.getMonth() + 1);
  }

  private buildCols(): void {
    const productHeader = this.newsOnly ? 'Quincy News product' : 'QRD/QPD product';
    this.cols = [
      {field: 'marketName', header: 'Market'},
      {header: productHeader},
      {field: 'market.name', header: 'Channel'},
      {field: 'type', header: 'Currently distributed'},
      {field: 'name', header: 'Description'}
    ];
  }

  public getDatesArray() {
    this.dateArray = [];
    const currentDate = this.minDateForCalendar;
    const stopDate = this.maxDateForCalendar;
    while (currentDate <= stopDate) {
      this.dateArray.push(this.datePipe.transform(currentDate, 'yyyy-MM-dd'));
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  ngOnInit() {
    this.utilsService.lockUi();
    this.newsOnly = !!this.route.snapshot.data['newsOnly'];
    this.buildCols();
    this.route.queryParams.subscribe(params => {
      const channelGetParameter = (params['channel']) ? params['channel'] : '';
      this.userChoicesActiveFilters = this.utilsService.loadUserPref('qrdSymbolsFilter', this.userChoicesFiltersDefaultQrdSymbols);
      this.initializeResearchForm(channelGetParameter, this.userChoicesActiveFilters);
    });
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.getQrdSymbolStartDate();
    });
    this.subscriptions.push(response$);
  }

  private getQrdSymbolStartDate() {
    this.symbolsService.getSymbolStartDates('qrd').subscribe({
      next: qrdSymbolsStartDates => {
        this.startDates = qrdSymbolsStartDates;
        this.getDatesArray();
        const disabledDatesStrings = this.dateArray.filter(item => this.startDates.indexOf(item) < 0);
        this.disabledDatesInCalendar = disabledDatesStrings.map(date => new Date(date));
        this.getQrdSymbolsInfo();
      }, error: () => this.displayLoadError()
    });
  }

  private getQrdSymbolsInfo() {
    this.symbolsService.getQrdSymbolInfo().subscribe({
      next: qrdSymbolsInfo => {
        if (qrdSymbolsInfo.length === 1) {
          this.qrdSymbolsInfo = qrdSymbolsInfo[0];
          this.qrdSymbolsInfoData = JSON.parse(this.qrdSymbolsInfo.data);
          this.getQrdSymbolList();
        } else {
          this.alertQedSymbolHasNotChanged = true;
          this.utilsService.unlockUi();
        }
      }, error: () => this.displayLoadError()
    });
  }

  private displayLoadError() {
    this.utilsService.unlockUi();
    this.utilsService.displayMessage('The symbol list could not be loaded', 'Please try again later.', 'error');
  }

  private getQrdSymbolList() {
    this.utilsService.lockUi();
    this.symbolsService.getMulticastDetailsList('qrd', this.startDates[0]).subscribe({
      next: multicastRes => {
        const permissionMap: Record<string, string[]> = {};
        Object.keys(multicastRes).forEach(key => {
          const item = multicastRes[key];
          const symbolName = item.qedSymbolName;
          const sfiId = item.sfiPermissioningId;
          if (symbolName && sfiId) {
            if (!permissionMap[symbolName]) {
              permissionMap[symbolName] = [];
            }
            if (!permissionMap[symbolName].includes(sfiId)) {
              permissionMap[symbolName].push(sfiId);
            }
          }
        });
        this.symbolsService.getQrdSymbol(this.newsOnly).subscribe({
          next: symbolsRes => {
            this.listQrdSymbols = symbolsRes.map((symbol: any) => {
              const sfiList = permissionMap[symbol.name] || [];
              return {
                ...symbol,
                sfiPermissioningId: sfiList,
              };
            });
            this.listQrdSymbolsWithSfi = this.listQrdSymbols.filter(s => s.sfiPermissioningId.length > 0 );
            this.filteredQrdSymbolList();
            if (this.listQrdSymbols.length !== 0) {
              this.createMarketNames();
              this.createQrdProductNames();
            } else {
              this.alertQedSymbolHasNotChanged = true;
            }
            this.utilsService.unlockUi();
          },
          error: () => {
            this.utilsService.unlockUi();
            this.redirectionToNotFound();
          }
        });
      },
      error: () => {
        this.utilsService.unlockUi();
        this.redirectionToNotFound();
      }
    });
  }

  private initializeResearchForm(channelGetParameter, userChoicesActiveFilters) {
    this.researchForm = this.formBuilder.group({
      qrdProduct: [userChoicesActiveFilters.qrdProduct],
      channel: [(channelGetParameter) ? channelGetParameter : userChoicesActiveFilters.channel],
      description: [userChoicesActiveFilters.description],
      pastDateRange1: [''],
      pastDateRange2: ['']

    });
    this.subscriptions.push(
      this.pastDateRange1Form.valueChanges.subscribe(() => this.verifyDateSelected(true)),
      this.pastDateRange2Form.valueChanges.subscribe(() => this.verifyDateSelected(false, true))
    );
  }

  trackByFn(index: number, qrdSymbol: QedSymbol): number {
    return qrdSymbol.symbolId ?? index;
  }

  redirectionToNotFound() {
    this.utilsService.redirectToNotFound();
  }

  private getForm(formName: string): AbstractControl {
    return this.researchForm.get(formName);
  }

  get qrdProductForm(): AbstractControl {
    return this.getForm('qrdProduct');
  }

  get channelForm(): AbstractControl {
    return this.getForm('channel');
  }

  get descriptionForm(): AbstractControl {
    return this.getForm('description');
  }

  get pastDateRange1Form(): AbstractControl {
    return this.getForm('pastDateRange1');
  }

  get pastDateRange2Form(): AbstractControl {
    return this.getForm('pastDateRange2');
  }

  resetTable(): void {
    this.qrdProductForm.setValue('');
    this.channelForm.setValue('');
    this.descriptionForm.setValue('');
    this.pastDateRange1Form.setValue('');
    this.pastDateRange2Form.setValue('');
    this.filteredQrdSymbolList();

    // The list of clients sent by the server is ordered by id
    this.listQrdSymbols.sort((symbol1, symbol2) => symbol1.symbolId - symbol2.symbolId);
  }

  private createMarketNames() {
    let list = [];
    list = this.listQrdSymbols
      .map(qrdSymbol => qrdSymbol.marketName)
      .sort();
    for (const cpt in list) {
      if (this.marketList.filter(uniqMarket => uniqMarket === list[cpt]).length === 0) {
        this.marketList.push(list[cpt]);
      }
    }
  }

  private createQrdProductNames() {
    const list = this.listQrdSymbols
      .filter(s => s.qedProduct)
      .map(function (qedSymbol) {
        return qedSymbol.qedProduct;
      }).sort();
    const qrdProducts = _.uniq(list);
    this.qrdProductDropdown = [];
    _.forEach(qrdProducts, (productName: string) => {
      this.qrdProductDropdown.push({
        label: productName,
        value: productName,
      });
    });
    return qrdProducts;
  }

  qrdSymbolList(market: string) {
    return this.listQrdSymbols.filter(qrdSymbol => qrdSymbol.marketName === market && this.filter(qrdSymbol));
  }

  noDataToDisplay() {
    return !!this.listQrdSymbols
      && this.marketList.every(market => this.qrdSymbolList(market).length === 0)
      && this.sfiSymbolList().length === 0;
  }

  sfiSymbolList() {
    return this.listQrdSymbolsWithSfi.filter(qrdSymbol => this.hasQrdProduct(qrdSymbol, this.qrdProductForm.value)
      && this.hasSfiChannel(qrdSymbol, this.channelForm.value)
      && this.hasDescription(qrdSymbol, this.descriptionForm.value));
  }

  filter(qrdSymbol) {
    return this.hasQrdProduct(qrdSymbol, this.qrdProductForm.value)
      && this.hasChannel(qrdSymbol, this.channelForm.value)
      && this.hasDescription(qrdSymbol, this.descriptionForm.value);
  }

  filteredQrdSymbolList() {
    if (!this.listQrdSymbols) {
      return [];
    }
    this.utilsService.saveUserData('qrdSymbolsFilter',
      {
        'qrdProduct': this.qrdProductForm.value,
        'channel': this.channelForm.value,
        'description': this.descriptionForm.value
      });
    this.filteredQrdSymbols = this.listQrdSymbols.filter(qrdSymbol => this.filter(qrdSymbol));
  }

  hasQrdProduct(qrdSymbol: QedSymbol, qrdProductFilterValue) {
    if (qrdProductFilterValue === '' || qrdProductFilterValue === null) {
      return true;
    }
    return qrdSymbol.qedProduct === qrdProductFilterValue;
  }

  hasChannel(qrdSymbol: QedSymbol, channelFilterValue) {
    if (channelFilterValue === '') {
      return true;
    }
    return !!qrdSymbol.market.toLowerCase().includes(channelFilterValue.toLowerCase());
  }

  hasSfiChannel(qrdSymbol, channelFilterValue) {
    if (channelFilterValue === '') {
      return true;
    }
    return qrdSymbol.sfiPermissioningId.some(sfiId => sfiId.toLowerCase().includes(channelFilterValue.toLowerCase()));
  }

  hasDescription(qrdSymbol: QedSymbol, descriptionFilterValue: any) {
    if (descriptionFilterValue === '') {
      return true;
    }
    return !!qrdSymbol.name.toLowerCase().includes(descriptionFilterValue.toLowerCase());
  }

  getQrdQpdProductName(qrdSymbol: QedSymbol) {
    if (!qrdSymbol.qedProduct) {
      return '';
    }
    return qrdSymbol.qedProduct;
  }

  redirectionToQrdMulticastListWithChannelGetParameter(channelName) {
    location.assign((this.newsOnly ? '/#/quincy-news-symbols/multicast' : '/#/qrd-symbols/multicast') + '?channel=' + channelName);
  }

  rangeDateNotSelected() {
    return !this.pastDateRange1Form.value
      || !this.pastDateRange2Form.value;
  }

  downloadOptionsChanged($event: Event) {
    // @ts-ignore
    this.downloadOption = $event.target.value;
  }

  verifyDateSelected(rangeDate1 = false, rangeDate2 = false) {
    if (!this.pastDateRange1Form.value || !this.pastDateRange2Form.value) {
      return;
    }
    this.symbolsService.verifyDateSelected(rangeDate1, rangeDate2, this.pastDateRange1Form, this.pastDateRange2Form);
  }


  downloadQrdSymbolsWithThisDateRange() {
    const startDate = new Date(this.pastDateRange1Form.value);
    const endDate = new Date(this.pastDateRange2Form.value);
    const startDateAsString = this.datePipe.transform(startDate, 'yyyy-MM-dd');
    const endDateAsString = this.datePipe.transform(endDate, 'yyyy-MM-dd');
    this.httpClient.get(environment.baseUrlWs +
      '/api/symbol-list-by-dates?service=qrd&start_date=' + startDateAsString + '&end_date=' + endDateAsString)
      .subscribe({
        next: (response) => {
          const blob = this.symbolsService.b64toBlob(response['file_content'], response['mime_type'], null);
          saveAs(blob, response['file_name']);
        }, error: (error: HttpResponse<any>) => {
          this.utilsService.handleErrorWebservice$(error);
        }
      });
  }

  downloadCurrentQrdSymbolList() {
    this.httpClient.get(environment.baseUrlWs +
      '/api/symbol-list?service=qrd&ui=1').subscribe({
      next: (response) => {
        const blob = this.symbolsService.b64toBlob(response['file_content'], response['mime_type'], null);
        saveAs(blob, response['file_name']);
      }, error: (error: HttpResponse<any>) => {
        this.utilsService.handleErrorWebservice$(error);
      }
    });
  }

  resetRangeDates() {
    this.pastDateRange1Form.setValue('');
    this.pastDateRange2Form.setValue('');
  }

  htmlDecode(qedSymbol: any) {
    return this.utilsService.htmlDecode(qedSymbol);
  }
}
